import { useEffect } from "react";
import { SITE_NAME, DEFAULT_LOCALE, TWITTER_CARD, buildCanonicalUrl } from "./config";

// Every managed tag carries data-seo-managed/data-seo-jsonld so it can be
// found and updated in place on the next route's render — one route is
// mounted at a time, so upserting by a stable selector (rather than adding/
// removing per mount) avoids a flash of empty <head> between client-side
// navigations while still never leaving stale content behind.

function upsertMeta(attr, name, content) {
  const selector = `meta[${attr}="${name}"][data-seo-managed]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("data-seo-managed", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(url) {
  const el = document.head.querySelector('link[rel="canonical"][data-seo-managed]');
  if (!url) {
    if (el) el.remove();
    return;
  }
  const link = el || document.createElement("link");
  link.setAttribute("rel", "canonical");
  link.setAttribute("data-seo-managed", "true");
  link.setAttribute("href", url);
  if (!el) document.head.appendChild(link);
}

function upsertJsonLd(schemas) {
  const list = schemas || [];
  // Drop any leftover script from a previous page that had more schemas
  // than this one, so stale JSON-LD never survives a client-side nav.
  document.head.querySelectorAll("script[data-seo-jsonld]").forEach((el) => {
    if (Number(el.getAttribute("data-seo-jsonld")) >= list.length) el.remove();
  });
  list.forEach((schema, index) => {
    let el = document.head.querySelector(`script[data-seo-jsonld="${index}"]`);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo-jsonld", String(index));
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
  });
}

/**
 * Drives document.title and every managed <head> tag from a plain SEO
 * descriptor. One call per top-level page component.
 * @param {Object} seo
 * @param {string} [seo.title]
 * @param {string} [seo.description]
 * @param {string} [seo.canonicalPath] - site-relative path, e.g. "/stores/abc123"; omit for noindex pages
 * @param {string} [seo.robots] - defaults to "index, follow"
 * @param {string} [seo.ogType] - defaults to "website"
 * @param {string} [seo.ogImage] - absolute URL
 * @param {Array<Object>} [seo.jsonLd] - JSON-LD objects to inject
 */
export function useSeo(seo) {
  const {
    title,
    description,
    canonicalPath,
    robots = "index, follow",
    ogType = "website",
    ogImage,
    jsonLd = [],
  } = seo || {};

  const canonicalUrl = canonicalPath ? buildCanonicalUrl(canonicalPath) : undefined;
  const jsonLdKey = JSON.stringify(jsonLd);

  useEffect(() => {
    if (title) document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:type", ogImage ? ogType : undefined);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", DEFAULT_LOCALE);

    upsertMeta("name", "twitter:card", ogImage ? TWITTER_CARD : undefined);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    upsertCanonical(canonicalUrl);
    upsertJsonLd(jsonLd);
    // jsonLd is compared via jsonLdKey (a new array/object reference every
    // render would otherwise re-run this effect on every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalUrl, robots, ogType, ogImage, jsonLdKey]);
}
